import { useTurnstile } from "../TurnstileContext";
import { getDeeplinkSearchParamsString, validatePassword } from "../utils";
import { Button, Input, Label, Message } from "./../base-ui/index";
import { PasswordCriteria } from "./PasswordCriteria";
import { PasswordRecoveryMfaChallenge } from "./PasswordRecoveryMfaChallenge";
import { usePasswordRecoveryMfaStepUp } from "@/components/mfa/usePasswordRecoveryMfaStepUp";
import { useToast } from "@/components/ui/use-toast";
import { recoverPasswordMutationOptions } from "@/hooks/mutations/auth/recoverPasswordMutationOptions";
import { setPasswordSignupMutationOptions } from "@/hooks/mutations/auth/setPasswordSignupMutationOptions";
import { assertNever } from "@/packages/ts-utils";
import { I18nVariables } from "@supabase/auth-ui-shared";
import { useMutation } from "@tanstack/react-query";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";

function UpdatePassword({
  i18n,
  passwordLimit = false,
}: {
  i18n?: I18nVariables;
  passwordLimit?: boolean;
}) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { toast } = useToast();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isCreatingPassword = searchParams.get("intent") === "signup";
  const email = searchParams.get("email");
  const { getToken } = useTurnstile();
  const stepUp = usePasswordRecoveryMfaStepUp();

  const passwordMutationOptions = isCreatingPassword
    ? setPasswordSignupMutationOptions({ getTurnstileToken: getToken })
    : recoverPasswordMutationOptions({ getTurnstileToken: getToken });
  const { mutate, isPending, error } = useMutation({
    ...passwordMutationOptions,
    onSuccess: () => {
      toast({
        title:
          i18n?.update_password?.confirmation_text ??
          "Your password has been updated",
        description: "Signing you in...",
        duration: 3000,
      });
      router.replace(`/auth${getDeeplinkSearchParamsString(searchParams)}`);
    },
  });

  const doPasswordsMatch = password === confirmPassword;

  const handlePasswordReset = () => {
    if (passwordLimit && password.length > 72) return;
    if (validatePassword(password)) return;
    if (!doPasswordsMatch) return;
    mutate({ newPassword: password });
  };

  const labels = i18n?.update_password;

  switch (stepUp.status) {
    case "checking":
      return (
        <div className="flex justify-center py-8">
          <div
            aria-label="Loading"
            className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-200 border-t-neutral-900"
          />
        </div>
      );
    case "failed":
      return (
        <div className="flex flex-col gap-4 text-left">
          <Message color="danger">{stepUp.error}</Message>
          <Button type="button" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </div>
      );
    case "challenge":
      return (
        <PasswordRecoveryMfaChallenge
          challenge={stepUp}
          isCreatingPassword={isCreatingPassword}
        />
      );
    case "passwordForm":
      break;
    default:
      return assertNever(stepUp);
  }

  return (
    <form
      id="auth-update-password"
      className="w-full"
      onSubmit={(e) => {
        e.preventDefault();
        handlePasswordReset();
      }}
    >
      <div className="flex flex-col space-y-4 text-left">
        {email && (
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              name="email"
              disabled
              placeholder={i18n?.forgotten_password?.email_input_placeholder}
              defaultValue={email}
              autoComplete="email"
            />
          </div>
        )}
        <div>
          <Label htmlFor="password">{labels?.password_label}</Label>
          <Input
            id="password"
            name="password"
            placeholder={labels?.password_input_placeholder}
            type="password"
            autoFocus
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="confirm-password">Confirm password</Label>
          <Input
            id="confirm-password"
            name="confirm-password"
            placeholder={
              isCreatingPassword
                ? "Re-enter your password"
                : "Re-enter your new password"
            }
            type="password"
            autoComplete="new-password"
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          {confirmPassword && !doPasswordsMatch && (
            <p className="text-sm text-red-500 mt-1">Passwords do not match</p>
          )}
        </div>
        <PasswordCriteria password={password} />
        <Button
          type="submit"
          color="primary"
          disabled={
            isPending ||
            validatePassword(password) !== null ||
            !doPasswordsMatch
          }
        >
          {isPending ? labels?.loading_button_label : labels?.button_label}
        </Button>
        {error && <Message color="danger">{error.message}</Message>}
      </div>
    </form>
  );
}

export { UpdatePassword };
